import React, {useState} from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import buildInfo from '../build-info.json';
import './Sidebar.css';

function Sidebar({ staffPermissions, token, characterName, firstName, onLogout }) {
    const [collapsed, setCollapsed] = useState(false);
    const navigate = useNavigate();

    const toggleSidebar = () => setCollapsed(!collapsed);

    const handleLogout = () => {
        onLogout();
        navigate('/login');
    };

    // "CharacterName (FirstName)" — or just the first name when there's no character.
    let displayName;
    if (characterName && firstName) displayName = `${characterName} (${firstName})`;
    else if (characterName) displayName = characterName;
    else displayName = firstName || 'Account';

    const avatarInitial = (displayName || '?').trim().charAt(0).toUpperCase();

    return (
        <div className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
            <button className="toggle-button" onClick={toggleSidebar}>
                {collapsed ? '▶' : '◀'}
            </button>

            {!collapsed && (
                <div className="sidebar-inner">
                    <div className="sidebar-header">
                        <NavLink to="/" className="landing-title-link">
                            <h1 className="landing-title">Chronicles</h1>
                        </NavLink>
                        <div className="sidebar-socials">
                            <a className="social-icon" href="https://larpchronicles.com/discord"
                               target="_blank" rel="noopener noreferrer" aria-label="Discord" title="Discord">
                                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.317 4.369a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
                            </a>
                            <span className="social-icon social-disabled" aria-label="Facebook" title="Facebook">
                                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                            </span>
                            <a className="social-icon" href="https://github.com/larpchronicles"
                               target="_blank" rel="noopener noreferrer" aria-label="GitHub" title="GitHub">
                                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>
                            </a>
                            <span className="social-icon social-disabled" aria-label="Email" title="Email">
                                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
                            </span>
                        </div>
                    </div>

                    <div className="sidebar-scroll">
                        <section className="resources-section">
                            <h2 className="resources-title">Tools</h2>
                            <ul className="resources-list">
                                <li><NavLink to="/character" className="internal-link">Character Manager</NavLink></li>
                            </ul>

                            {staffPermissions && (
                                <>
                                <h2 className="resources-title">Staff Tools</h2>
                                <ul className="resources-list">
                                    <li><NavLink to="/event-manager" className="internal-link">Event Manager</NavLink></li>
                                    {/*<li><NavLink to="/skills" className="internal-link">Skill Editor</NavLink></li>*/}
                                    <li><NavLink to="/transform" className="internal-link">Transform Editor</NavLink></li>
                                </ul>
                                </>
                            )}

                            <h2 className="resources-title">Events</h2>
                            <ul className="resources-list">
                                <li><NavLink to="/schedule" className="internal-link">Event Schedule</NavLink></li>
                                <li><a href="https://larpchronicles.com/registration" target=" _blank" rel=" noopener noreferrer">Event Registration</a></li>
                                <li><a href="https://larpchronicles.com/feedback" target=" _blank" rel=" noopener noreferrer">Post-Event Feedback</a></li>
                            </ul>

                            <h2 className="resources-title">Game Resources</h2>
                            <ul className="resources-list">
                                <li><NavLink to="/rulebook" className="internal-link">Rulebook</NavLink></li>
                                {/*<li><a href="https://larpchronicles.com/battleboard" target=" _blank" rel=" noopener noreferrer">Battleboards</a></li>*/}
                            </ul>

                            <h2 className="resources-title">Player Resources</h2>
                            <ul className="resources-list">
                                <li><a href="https://larpchronicles.com/discord" target="_blank" rel="noopener noreferrer">Chronicles Discord Server</a></li>
                                <li><a href="https://larpchronicles.com/codeofconduct" target=" _blank" rel=" noopener noreferrer">Code of Conduct</a></li>
                                <li><a href="https://larpchronicles.com/liabilitywaiver" target=" _blank" rel=" noopener noreferrer">Liability Waiver</a></li>
                            </ul>
                        </section>
                        <div className="landing-version">v{buildInfo.full}</div>
                    </div>

                    <div className="sidebar-footer">
                        {token ? (
                            <div className="sidebar-user">
                                <div className="sidebar-user-avatar">{avatarInitial}</div>
                                <span className="sidebar-user-name" title={displayName}>{displayName}</span>
                                <button className="sidebar-logout-btn" onClick={handleLogout}>
                                    Log Out
                                </button>
                            </div>
                        ) : (
                            <button className="sidebar-login-btn" onClick={() => navigate('/login')}>
                                Sign In
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Sidebar;
